import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../supabase.config';
import { MdAccessTime } from 'react-icons/md';
import SharedDetailsPage from '../SharedDetailedPage';

// IDs for 24-hour locations
const TWENTY_FOUR_HOUR_BUILDING_IDS = [
  'dwZchMFE1iGIzIc30xi8', // 24 Lounge
  'BvhxSg23XGHr2Hfu3LB9'  // 24 Lounge Study Rooms
];

const DEFAULT_LISTING = {
  name: 'Unnamed Study Spot',
  spaceType: 'Study Space',
  noiseLevel: 'Moderate',
  description: 'No description available',
  features: [],
  imageURL: [],
  weekdayAndHours: Array(7).fill('7:00 AM - 11:00 PM'),
};

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const is24HourLocation = (id, name) => {
  return TWENTY_FOUR_HOUR_BUILDING_IDS.includes(id) || 
         name?.includes('24 Lounge') ||
         name?.includes('24 Lounge Study Rooms');
};

const isLibrary = (data) => {
  // Check both name and location fields for "Library"
  return (data?.name?.includes('Library') || 
          data?.location?.includes('Library') ||
          data?.building === 'University Library');
};

const getLocationHours = (id, data, dayIndex) => {
  if (is24HourLocation(id, data?.name)) {
    return 'Open 24 Hours';
  } else if (isLibrary(data)) {
    // Library hours: Mon-Fri 8:30 AM - 10:00 PM, Sat-Sun 11:00 AM - 5:00 PM
    return dayIndex >= 5 ? '11:00 AM - 5:00 PM' : '8:30 AM - 10:00 PM';
  } else {
    return '7:00 AM - 11:00 PM'; // Default hours
  }
};

const OperatingHours = ({ data, buildingId }) => {
  return (
    <div className="bg-[#111] border border-white/10 rounded-2xl p-6 sticky top-24">
      <div className="flex items-center gap-2 mb-6">
        <MdAccessTime className="w-5 h-5 text-purple-400" />
        <h2 className="text-lg font-bold text-white">Operating Hours</h2>
      </div>
      <div className="space-y-3">
        {weekdays.map((day, index) => (
          <div
            key={day}
            className={`flex justify-between text-sm py-2 border-b border-white/5 last:border-0 ${
              new Date().getDay() === (index + 1) % 7
                ? 'text-white font-medium'
                : 'text-gray-400'
            }`}
          >
            <span>{day}</span>
            <span>{getLocationHours(buildingId, data, index)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ListingPage = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        // Fetch study spot with building and photos from Supabase
        const { data, error } = await supabase
          .from('StudySpot')
          .select(`
            *,
            building:Building(*),
            photos:Photo(*)
          `)
          .eq('id', id)
          .single();

        if (error) throw error;

        if (data) {
          const defaultHours = weekdays.map((_, index) => 
            getLocationHours(id, data, index)
          );

          // Transform to expected format with legacy compatibility
          const listingData = {
            ...DEFAULT_LISTING,
            id: data.id,
            name: data.name,
            building: data.building?.name || '',
            location: data.location,
            description: data.description,
            features: data.features || [],
            noiseLevel: data.noiseLevel,
            spaceType: data.spaceType,
            // Support both old imageURL and new photos format
            imageURL: data.photos?.map(p => p.url) || [],
            photos: data.photos || [],
            weekdayAndHours: defaultHours
          };
          setListing(listingData);
        }
      } catch (error) {
        console.error("Error fetching listing:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id]);

  return (
    <SharedDetailsPage
      data={listing}
      type="listing"
      RightColumnComponent={<OperatingHours data={listing || DEFAULT_LISTING} buildingId={id} />}
      loading={loading}
    />
  );
};

export default ListingPage;
